import React, { useEffect, useState, useCallback } from 'react';
import adminApi from '../../api/client';
import LinkingAssistant from './LinkingAssistant';

interface Genre {
  id: string;
  name_en: string;
  name_si: string;
  slug: string;
  parent_id: string | null;
  sort_order: number;
  is_active: boolean;
  linked_books_count: number;
}

interface PaginatedGenres {
  total: number;
  page: number;
  per_page: number;
  items: Genre[];
}

const Toast = ({ message, type, onClose }: { message: string, type: 'success' | 'error', onClose: () => void }) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);
  
  if (!message) return null;
  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded shadow-lg text-white font-medium z-50 ${type === 'success' ? 'bg-[#1D9E75]' : 'bg-[#D85A30]'}`}>
      {message}
    </div>
  );
};

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, text, confirmText, confirmColor }: any) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <h3 className="text-xl font-bold text-[#085041] mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{text}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
          <button 
            onClick={onConfirm} 
            className={`px-4 py-2 text-white rounded hover:bg-opacity-90 font-medium bg-[${confirmColor}]`}
            style={{ backgroundColor: confirmColor }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const GenreModal = ({ isOpen, onClose, onSave, genre, allGenres }: { isOpen: boolean, onClose: () => void, onSave: (data: any) => void, genre: Genre | null, allGenres: Genre[] }) => {
  const [formData, setFormData] = useState({
    name_en: '',
    name_si: '',
    slug: '',
    parent_id: '',
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    if (genre) {
      setFormData({
        name_en: genre.name_en || '',
        name_si: genre.name_si || '',
        slug: genre.slug || '',
        parent_id: genre.parent_id || '',
        sort_order: genre.sort_order || 0,
        is_active: genre.is_active !== false
      });
    } else {
      setFormData({ name_en: '', name_si: '', slug: '', parent_id: '', sort_order: 0, is_active: true });
    }
  }, [genre, isOpen]);

  if (!isOpen) return null;

  // Filter possible parents (prevent circular logic mostly, or just show root/all genres)
  const parentOptions = allGenres.filter(g => g.id !== genre?.id);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-xl">
        <h3 className="text-xl font-bold text-[#085041] mb-4">{genre ? 'Edit Genre' : 'Add Genre'}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name (English) <span className="text-red-500">*</span></label>
            <input type="text" className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75]" value={formData.name_en} onChange={e => setFormData({...formData, name_en: e.target.value})} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name (Sinhala) <span className="text-red-500">*</span></label>
            <input type="text" className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75]" value={formData.name_si} onChange={e => setFormData({...formData, name_si: e.target.value})} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug <span className="text-red-500">*</span> {genre && <span className="text-xs text-gray-400 font-normal">(Cannot update)</span>}</label>
            <input type="text" disabled={!!genre} className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75] disabled:bg-gray-100 disabled:text-gray-500" value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category {genre && <span className="text-xs text-gray-400 font-normal">(Cannot update)</span>}</label>
            <select disabled={!!genre} className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75] disabled:bg-gray-100 disabled:text-gray-500" value={formData.parent_id} onChange={e => setFormData({...formData, parent_id: e.target.value})}>
              <option value="">-- None (Root) --</option>
              {parentOptions.map(p => (
                <option key={p.id} value={p.id}>{p.name_en} ({p.name_si})</option>
              ))}
            </select>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75]" value={formData.sort_order} onChange={e => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})} />
            </div>
            {genre && (
              <div className="flex-1 flex items-end pb-2">
                <label className="flex items-center gap-2 text-[#D85A30]">
                  <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
                  <span className="text-sm font-medium">Is Active</span>
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
          <button 
            onClick={() => onSave(formData)} 
            disabled={!formData.name_en || !formData.name_si || !formData.slug}
            className="px-4 py-2 bg-[#085041] text-white rounded hover:bg-opacity-90 font-medium disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default function GenresPanel() {
  const [activeView, setActiveView] = useState<'browse' | 'link'>('browse');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [parentIdFilter, setParentIdFilter] = useState('all'); // 'all', 'root', or a specific parent_id
  const perPage = 20;

  const [browseData, setBrowseData] = useState<PaginatedGenres>({ total: 0, page: 1, per_page: 20, items: [] });
  // Used for populating parent selector in the modal & filter dropdown
  const [allGenresList, setAllGenresList] = useState<Genre[]>([]); 
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({ show: false, message: '', type: 'success' });
  const [editModal, setEditModal] = useState<{show: boolean, genre: Genre | null}>({ show: false, genre: null });
  const [deleteModal, setDeleteModal] = useState<{show: boolean, id: string | null}>({ show: false, id: null });

  useEffect(() => {
    // Fetch all root genres once to populate parent dropdowns
    const fetchRootGenres = async () => {
      try {
        const res = await adminApi.get('/admin/master/genres?parent_id=all&per_page=500');
        if (res.data?.items) {
          setAllGenresList(res.data.items);
        }
      } catch (e) {
        console.error("Failed to load root genres", e);
      }
    };
    fetchRootGenres();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchBrowse = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `/admin/master/genres?q=${debouncedSearch}&page=${page}&per_page=${perPage}`;
      if (parentIdFilter === 'root') {
        url += '&parent_id=root';
      } else if (parentIdFilter !== 'all') {
        url += `&parent_id=${parentIdFilter}`;
      }
      
      const res = await adminApi.get(url);
      setBrowseData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch genres');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, parentIdFilter]);

  useEffect(() => {
    fetchBrowse();
  }, [fetchBrowse]);

  const showToast = (message: string, type: 'success' | 'error') => setToast({ show: true, message, type });

  const handleSaveGenre = async (data: any) => {
    try {
      if (editModal.genre) {
        // Update: slug and parent_id are ignored by backend, so we only send allowed fields
        const payload = {
          name_en: data.name_en || null,
          name_si: data.name_si || null,
          sort_order: data.sort_order,
          is_active: data.is_active
        };
        await adminApi.patch(`/admin/master/genres/${editModal.genre.id}`, payload);
        showToast('Genre updated successfully', 'success');
      } else {
        // Create
        const payload = {
          name_en: data.name_en,
          name_si: data.name_si,
          slug: data.slug,
          parent_id: data.parent_id || null,
          sort_order: data.sort_order || 0
        };
        await adminApi.post('/admin/master/genres', payload);
        showToast('Genre added successfully', 'success');
      }
      setEditModal({ show: false, genre: null });
      fetchBrowse();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to save genre', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.id) return;
    try {
      await adminApi.delete(`/admin/master/genres/${deleteModal.id}`);
      showToast('Genre deactivated successfully', 'success');
      setDeleteModal({ show: false, id: null });
      fetchBrowse();
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to deactivate genre';
      showToast(detail, 'error');
      setError(detail);
      setDeleteModal({ show: false, id: null });
    }
  };

  const getParentName = (parentId: string | null) => {
    if (!parentId) return '— Root —';
    const parent = allGenresList.find(g => g.id === parentId);
    return parent ? `${parent.name_en}` : parentId;
  };

  const totalPages = Math.ceil((browseData.total || 0) / perPage);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex bg-gray-200 rounded-full p-1">
          <button 
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${activeView === 'browse' ? 'bg-white text-[#085041] shadow' : 'text-gray-500'}`}
            onClick={() => setActiveView('browse')}
          >
            Browse All
          </button>
          <button 
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${activeView === 'link' ? 'bg-white text-[#085041] shadow' : 'text-gray-500'}`}
            onClick={() => setActiveView('link')}
          >
            Link Records
          </button>
        </div>

        {activeView === 'browse' && (
          <button 
            onClick={() => setEditModal({ show: true, genre: null })}
            className="px-4 py-2 bg-[#085041] text-white rounded font-medium hover:bg-opacity-90"
          >
            + Add Genre
          </button>
        )}
      </div>

      {error && <div className="bg-[#D85A30]/10 border border-[#D85A30] text-[#D85A30] px-4 py-3 rounded mb-6">{error}</div>}

      {activeView === 'browse' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row gap-4">
          <input 
            type="text" 
            placeholder="Search genres..." 
            className="w-full md:w-1/3 border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select 
            className="w-full md:w-1/3 border border-gray-300 rounded p-2 focus:outline-none focus:border-[#1D9E75]"
            value={parentIdFilter}
            onChange={(e) => { setParentIdFilter(e.target.value); setPage(1); }}
          >
            <option value="all">Show: All</option>
            <option value="root">Show: Root categories only</option>
            <optgroup label="Show sub-categories of:">
              {allGenresList.filter(g => !g.parent_id).map(g => (
                <option key={g.id} value={g.id}>{g.name_en} ({g.name_si})</option>
              ))}
            </optgroup>
          </select>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-sm border-b border-gray-200">
                <th className="p-4 font-semibold">Name</th>
                <th className="p-4 font-semibold">Slug</th>
                <th className="p-4 font-semibold">Parent</th>
                <th className="p-4 font-semibold">Sort Order</th>
                <th className="p-4 font-semibold">Linked Books</th>
                <th className="p-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && browseData.items.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-gray-500">Loading...</td></tr>
              ) : browseData.items.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-gray-500">No records found</td></tr>
              ) : (
                browseData.items.map(genre => (
                  <tr key={genre.id} className={`hover:bg-gray-50 ${!genre.is_active ? 'opacity-60' : ''}`}>
                    <td className="p-4">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {genre.name_en} 
                        {!genre.is_active && <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded">Inactive</span>}
                      </div>
                      <div className="text-xs text-gray-500">{genre.name_si}</div>
                    </td>
                    <td className="p-4 text-sm text-gray-600 font-mono">{genre.slug}</td>
                    <td className="p-4 text-sm text-gray-600">
                      <span className={!genre.parent_id ? 'text-[#BA7517] font-medium' : ''}>
                        {getParentName(genre.parent_id)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-600">{genre.sort_order}</td>
                    <td className="p-4 text-gray-600">{genre.linked_books_count}</td>
                    <td className="p-4 text-right">
                      <button onClick={() => setEditModal({ show: true, genre })} className="text-[#085041] hover:underline mr-4 font-medium text-sm">Edit</button>
                      <button onClick={() => setDeleteModal({ show: true, id: genre.id })} className="text-[#D85A30] hover:underline font-medium text-sm">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
            <div>Total: {browseData.total}</div>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
              <span className="px-3 py-1">Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>
      )}

      {activeView === 'link' && (
        <LinkingAssistant entityType="genres" />
      )}

      <Toast message={toast.show ? toast.message : ''} type={toast.type} onClose={() => setToast(prev => ({ ...prev, show: false }))} />
      
      <GenreModal 
        isOpen={editModal.show} 
        genre={editModal.genre} 
        allGenres={allGenresList}
        onClose={() => setEditModal({ show: false, genre: null })} 
        onSave={handleSaveGenre} 
      />
      
      <ConfirmModal 
        isOpen={deleteModal.show} 
        title="Deactivate Genre"
        text="Deactivate this genre? This does not delete data, only hides it from search. (Fails if books or sub-genres are linked)"
        confirmText="Deactivate"
        confirmColor="#D85A30"
        onClose={() => setDeleteModal({ show: false, id: null })}
        onConfirm={handleDelete}
      />
    </div>
  );
}
